import { useCallback, useState } from "react";

import { IconBin, IconPlus } from "@synthesis/shared-icons";
import { downloadFinalDecisionDocumentFile } from "@synthesis/shared-services";
import { useDeleteRemark } from "@synthesis/shared-services/internal/application/hooks";
import { Button, setPromiseSnack } from "@synthesis/shared-ui";
import { useDownload } from "@synthesis/shared-utils";
import { useQueryClient } from "@tanstack/react-query";

import { AttachmentFileRow, type AttachmentFileMeta } from "../../../../components/AttachmentFileRow";
import type { ApplicationDetailFinalDecisionRemark } from "../../application-detail-page.lib";
import { DetailFieldRow } from "../detail-field-row";
import { FINAL_DECISION_REMARK_DELETE_SNACK_MESSAGES, invalidateFinalDecisionVersionQueries } from "./final-decision.lib";
import { FinalDecisionRemarkModal } from "./final-decision-remark-modal";

type FinalDecisionRemarksSectionProps = {
  requestId: string;
  remarks: ApplicationDetailFinalDecisionRemark[];
  canManageRemarks: boolean;
};

const toAttachmentFileMeta = (file: ApplicationDetailFinalDecisionRemark["documents"][number]): AttachmentFileMeta => ({
  id: file.id,
  baseName: file.baseName,
  ext: file.ext,
});

export const FinalDecisionRemarksSection = ({ requestId, remarks, canManageRemarks }: FinalDecisionRemarksSectionProps) => {
  const queryClient = useQueryClient();
  const downloadFile = useDownload();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingRemarkId, setDeletingRemarkId] = useState<string | null>(null);

  const deleteRemarkMutation = useDeleteRemark({ mutation: { mutationKey: ["final-decision-remark-delete", requestId] } });

  const handleDownload = useCallback(
    async (fileMeta: AttachmentFileMeta) => {
      const blob = await downloadFinalDecisionDocumentFile({ requestId, fileId: fileMeta.id });
      downloadFile(
        {
          id: fileMeta.id,
          baseName: fileMeta.baseName,
          ...(fileMeta.ext ? { ext: fileMeta.ext } : {}),
        },
        blob,
      );
    },
    [downloadFile, requestId],
  );

  const handleDeleteRemark = (remarkId: string) => {
    if (deletingRemarkId) {
      return;
    }

    setDeletingRemarkId(remarkId);

    const promise = (async () => {
      await deleteRemarkMutation.mutateAsync({ path: { requestId, remarkId } });
      await invalidateFinalDecisionVersionQueries(queryClient, requestId);
    })();

    setPromiseSnack({
      promise,
      promiseSettings: {
        title: FINAL_DECISION_REMARK_DELETE_SNACK_MESSAGES,
      },
    });

    promise
      .catch(() => undefined)
      .finally(() => {
        setDeletingRemarkId(null);
      });
  };

  if (remarks.length === 0 && !canManageRemarks) {
    return null;
  }

  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="subtitle-base text-type-common">Замечание</h3>
        {canManageRemarks ? (
          <Button type="button" theme="text" size="sm" leftIcon={<IconPlus className="size-5" aria-hidden />} onClick={() => setIsModalOpen(true)}>
            Добавить замечание
          </Button>
        ) : null}
      </div>

      {remarks.map((remark) => (
        <div key={remark.id} className="border-neutrals-90 flex flex-col rounded-2xl border px-4">
          <DetailFieldRow label="Автор замечания">{remark.authorName || "—"}</DetailFieldRow>
          <DetailFieldRow label="Дата добавления">{remark.createdAt}</DetailFieldRow>
          <DetailFieldRow label="Замечание">{remark.comment}</DetailFieldRow>
          <DetailFieldRow label="Приложения к замечанию" className="border-b-0">
            {remark.documents.length === 0 ? (
              "—"
            ) : (
              <ul className="flex list-none flex-col gap-1">
                {remark.documents.map((file) => (
                  <AttachmentFileRow key={file.id} fileMeta={toAttachmentFileMeta(file)} download={handleDownload} />
                ))}
              </ul>
            )}
          </DetailFieldRow>
          {canManageRemarks ? (
            <div className="border-neutrals-90 py-3">
              <Button
                type="button"
                theme="text"
                size="xs"
                disabled={deletingRemarkId !== null}
                leftIcon={<IconBin className="size-4" aria-hidden />}
                onClick={() => handleDeleteRemark(remark.id)}
              >
                Удалить замечание
              </Button>
            </div>
          ) : null}
        </div>
      ))}

      {isModalOpen ? <FinalDecisionRemarkModal requestId={requestId} onClose={() => setIsModalOpen(false)} /> : null}
    </div>
  );
};
